import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from "firebase/firestore";
import { startOfDayInTimezone } from "../utils/dateFormat";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { BarChart2, Truck, ClipboardList, AlertTriangle } from "lucide-react";

export default function CompanyAnalytics({ company }) {
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");
  const [period,          setPeriod]          = useState("7days");
  const [companyTimezone, setCompanyTimezone] = useState("Asia/Dubai");
  const [analytics,  setAnalytics]  = useState(null);
  const [chartData,  setChartData]  = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [events,     setEvents]     = useState([]);

  useEffect(() => {
    if (!company.id) return;
    loadAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company.id, period]);

  async function loadAnalytics() {
    setLoading(true);
    setError("");
    try {
      const configSnap = await getDoc(doc(db, "companies", company.id, "config", "settings"));
      const tz = configSnap.exists() ? (configSnap.data().timezone || "Asia/Dubai") : "Asia/Dubai";
      setCompanyTimezone(tz);

      const days      = period === "7days" ? 7 : period === "30days" ? 30 : 90;
      const todayStart = startOfDayInTimezone(tz);
      const startDate  = new Date(todayStart.getTime() - days * 86400000);

      const eventsSnap = await getDocs(
        query(
          collection(db, "companies", company.id, "events"),
          where("timestamp", ">=", startDate),
          orderBy("timestamp", "desc"),
          limit(5000)
        )
      );
      const allEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setEvents(allEvents);

      const checkins    = allEvents.filter(e => e.type === "checkin");
      const alerts      = allEvents.filter(e => e.type?.includes("alert") || e.type === "zone_alert");
      const speedAlerts = allEvents.filter(e => e.type === "speed_alert");
      const longStops   = allEvents.filter(e => e.type === "long_stop_alert");

      setAnalytics({
        totalEvents:       allEvents.length,
        totalCheckins:     checkins.length,
        totalAlerts:       alerts.length,
        speedAlerts:       speedAlerts.length,
        longStopAlerts:    longStops.length,
        avgCheckinsPerDay: checkins.length > 0 ? (checkins.length / days).toFixed(1) : 0,
        uniqueVendors:     new Set(checkins.map(e => e.vendor_id).filter(Boolean)).size,
        uniqueClients:     new Set(checkins.map(e => e.client_id).filter(Boolean)).size,
      });

      // Daily chart data — bucket events by day in company timezone
      const byDate = {};
      for (let i = days - 1; i >= 0; i--) {
        const d      = new Date(todayStart.getTime() - i * 86400000);
        const dayKey = d.toLocaleDateString("en-CA", { timeZone: tz });
        const label  = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: tz });
        byDate[dayKey] = { date: label, visits: 0, alerts: 0 };
      }
      allEvents.forEach(e => {
        if (!e.timestamp) return;
        const d      = e.timestamp.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
        const dayKey = d.toLocaleDateString("en-CA", { timeZone: tz });
        if (!byDate[dayKey]) return;
        if (e.type === "checkin") byDate[dayKey].visits++;
        if (e.type?.includes("alert") || e.type === "zone_alert") byDate[dayKey].alerts++;
      });
      setChartData(Object.values(byDate));

      // Top vendors
      const vendorCounts = {};
      checkins.forEach(e => {
        if (!e.vendor_id) return;
        vendorCounts[e.vendor_id] = vendorCounts[e.vendor_id] || { name: e.vendor_name || e.vendor_id, count: 0 };
        vendorCounts[e.vendor_id].count++;
      });
      setTopVendors(Object.values(vendorCounts).sort((a, b) => b.count - a.count).slice(0, 5));

      // Top clients
      const clientCounts = {};
      checkins.forEach(e => {
        if (!e.client_id) return;
        clientCounts[e.client_id] = clientCounts[e.client_id] || { name: e.client_name || e.client_id, count: 0 };
        clientCounts[e.client_id].count++;
      });
      setTopClients(Object.values(clientCounts).sort((a, b) => b.count - a.count).slice(0, 5));

    } catch (err) {
      setError(`Failed to load analytics: ${err.message}${err.code ? ` (${err.code})` : ""}`);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">Loading analytics...</p>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
      <p className="text-red-700 text-sm font-medium">{error}</p>
      <button onClick={loadAnalytics} className="mt-3 text-sm text-red-600 underline">Retry</button>
    </div>
  );

  const dayCount = period === "7days" ? 7 : period === "30days" ? 30 : 90;

  return (
    <div className="space-y-4">

      {/* Header + Period Selector */}
      <div className="bg-white rounded-xl shadow-sm p-5 flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <BarChart2 className="w-4 h-4" /> Usage Analytics
        </h3>
        <div className="flex gap-1">
          {[{ id: "7days", label: "7 Days" }, { id: "30days", label: "30 Days" }, { id: "90days", label: "90 Days" }].map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                period === p.id ? "bg-yellow-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-green-600">{analytics?.totalCheckins || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Total Check-ins</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-blue-600">{analytics?.avgCheckinsPerDay || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Avg Visits / Day</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-purple-600">{analytics?.uniqueVendors || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Active Vendors</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-red-600">{analytics?.totalAlerts || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Total Alerts</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-orange-600">{analytics?.speedAlerts || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Speed Alerts</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-yellow-600">{analytics?.longStopAlerts || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Long Stop Alerts</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-gray-700">{analytics?.uniqueClients || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Clients Visited</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <p className="text-2xl font-bold text-gray-700">{analytics?.totalEvents || 0}</p>
          <p className="text-gray-500 text-sm mt-0.5">Total Events</p>
        </div>
      </div>

      {/* Daily Activity Chart */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">
          Daily Activity — Last {dayCount} Days
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }}
              interval={period === "7days" ? 0 : period === "30days" ? 4 : 13} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="visits" fill="#10B981" radius={[3, 3, 0, 0]} name="Visits" />
            <Bar dataKey="alerts" fill="#EF4444" radius={[3, 3, 0, 0]} name="Alerts" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Vendors + Top Clients */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Truck className="w-4 h-4" /> Top Vendors by Visits
          </h3>
          {topVendors.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">No visits recorded</p>
          ) : (
            <div className="space-y-3">
              {topVendors.map((vendor, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate">{vendor.name}</span>
                      <span className="text-sm font-bold text-green-600 ml-2 flex-shrink-0">{vendor.count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-green-500 h-1.5 rounded-full"
                        style={{ width: `${(vendor.count / topVendors[0].count) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Most Visited Clients
          </h3>
          {topClients.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">No visits recorded</p>
          ) : (
            <div className="space-y-3">
              {topClients.map((client, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate">{client.name}</span>
                      <span className="text-sm font-bold text-blue-600 ml-2 flex-shrink-0">{client.count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full"
                        style={{ width: `${(client.count / topClients[0].count) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alert Breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Alert Breakdown
        </h3>
        {(analytics?.totalAlerts || 0) === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">No alerts in this period — great performance!</p>
        ) : (
          <div className="space-y-3">
            {[
              { label: "Speed Alerts",        value: analytics?.speedAlerts    || 0,                                      color: "bg-red-500"    },
              { label: "Long Stop Alerts",     value: analytics?.longStopAlerts || 0,                                      color: "bg-orange-500" },
              { label: "Zone Alerts",          value: events.filter(e => e.type === "zone_alert").length,                  color: "bg-blue-500"   },
              { label: "Outside Hours Alerts", value: events.filter(e => e.type === "outside_hours_alert").length,         color: "bg-purple-500" },
            ].filter(item => item.value > 0).map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className="text-sm font-bold text-gray-800">{item.value}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`${item.color} h-2 rounded-full`}
                      style={{ width: `${analytics.totalAlerts > 0 ? (item.value / analytics.totalAlerts) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
